import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../routes/routeConfig';
import { fallbackAcademyDocs, fetchAcademyDocsResult } from './academyData';
import { getBookCover } from '../books/bookCovers';
import './academy.css';

const BOOK_CATEGORY = '推荐书籍';
const ALL_CATEGORY = '全部';
const PAGE_SIZE = 10;

// 分类 slug ↔ 真实分类名 + 页面标题 + 量词
const CATEGORY_MAP = {
  books: { name: '推荐书籍', title: '书籍库', unit: '本' },
  articles: { name: ALL_CATEGORY, title: '文档库', unit: '篇' },
  guides: { name: '交易所教程', title: '教程库', unit: '篇' },
};

function categoryFromPath(pathname) {
  if (pathname.endsWith('/books')) return 'books';
  if (pathname.endsWith('/articles')) return 'articles';
  if (pathname.endsWith('/guides')) return 'guides';
  return undefined;
}

const stripMarkdown = (content = '') => (
  content
    .replace(/!\[[^\]]*]\([^)]*\)/g, '')
    .replace(/\[[^\]]*]\([^)]*\)/g, '')
    .replace(/[#>*_`|~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
);

const getAuthor = (content = '') => {
  const match = content.match(/作者[：:]\s*([^)\n]+(?:\([^)]*\))?)/);
  return match?.[1]?.replace(/\*\*/g, '').trim() || '';
};

const normalizeText = (value = '') => value.trim().toLowerCase();

const getInitialCategory = (category, search) => {
  const params = new URLSearchParams(search || '');
  const queryCategory = params.get('category');
  if (queryCategory) return queryCategory;
  if (category === 'guides') return CATEGORY_MAP.guides.name;
  return ALL_CATEGORY;
};

export default function DocLibrary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { category: categoryParam } = useParams();
  const category = categoryParam || categoryFromPath(location.pathname);
  const meta = CATEGORY_MAP[category];
  const [libraryDocs, setLibraryDocs] = useState(fallbackAcademyDocs);
  const [activeCategory, setActiveCategory] = useState(() => getInitialCategory(category, location.search));
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const isBook = category === 'books';
  const docCategories = useMemo(
    () => [
      ALL_CATEGORY,
      ...Array.from(new Set(libraryDocs.filter((d) => d.category !== BOOK_CATEGORY).map((d) => d.category))).filter(Boolean),
    ],
    [libraryDocs]
  );
  const items = useMemo(
    () => {
      if (!meta) return [];
      if (isBook) return libraryDocs.filter((d) => d.category === meta.name);

      const query = normalizeText(searchTerm);
      return libraryDocs
        .filter((d) => d.category !== BOOK_CATEGORY)
        .filter((d) => activeCategory === ALL_CATEGORY || d.category === activeCategory)
        .filter((d) => {
          if (!query) return true;
          return normalizeText(`${d.title} ${d.summary || ''} ${stripMarkdown(d.content || '')}`).includes(query);
        });
    },
    [activeCategory, isBook, meta, libraryDocs, searchTerm]
  );

  useEffect(() => {
    if (category === 'books') return undefined;

    let cancelled = false;

    fetchAcademyDocsResult().then((result) => {
      if (!cancelled) {
        setLibraryDocs(result.docs);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [category]);

  useEffect(() => {
    setActiveCategory(getInitialCategory(category, location.search));
    setSearchTerm('');
    setPage(1);
  }, [category, location.search]);

  useEffect(() => {
    setPage(1);
  }, [activeCategory, searchTerm]);

  if (!meta) {
    return (
      <main className="academy-doc-library">
        <button type="button" className="academy-library-back" onClick={() => navigate(ROUTES.academy)}>← 返回财富学院</button>
        <p className="academy-library-empty">未找到该资料库。</p>
      </main>
    );
  }

  const goDoc = (id) => navigate(`/docs/${id}`);
  const totalPages = !isBook && items.length > PAGE_SIZE ? Math.ceil(items.length / PAGE_SIZE) : 1;
  const safePage = Math.min(page, totalPages);
  const visibleItems = !isBook && totalPages > 1
    ? items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
    : items;

  return (
    <main className="academy-doc-library">
      <button type="button" className="academy-library-back" onClick={() => navigate(ROUTES.academy)}>← 返回财富学院</button>

      <header className="academy-library-head">
        <span className="academy-sec-label">{meta.title}</span>
        <h1 className="academy-library-title">{meta.title}</h1>
        <p className="academy-library-count">共 {items.length} {meta.unit}</p>
      </header>

      {!isBook && (
        <section className="academy-library-tools" aria-label="文档筛选">
          <label className="academy-library-search">
            <span>搜索文档</span>
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="搜索标题、摘要或正文"
            />
          </label>
          <div className="academy-library-filters" aria-label="文档分类">
            {docCategories.map((item) => (
              <button
                type="button"
                key={item}
                aria-pressed={activeCategory === item}
                onClick={() => setActiveCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </section>
      )}

      <div className={`academy-library-grid${isBook ? ' is-books' : ''}`}>
        {visibleItems.map((item, index) => (
          <button type="button" className="academy-library-card" key={item.id} onClick={() => goDoc(item.id)}>
            {isBook ? (
              <div className="academy-library-cover">
                <img src={getBookCover(item.id)} alt={`${item.title} 封面`} />
              </div>
            ) : (
              <div className="academy-library-card-top">
                <span className="academy-library-index">{String((safePage - 1) * PAGE_SIZE + index + 1).padStart(2, '0')}</span>
                <span className="academy-library-category">{item.category}</span>
              </div>
            )}
            <div className="academy-library-card-body">
              <h3>{item.title}</h3>
              {isBook && getAuthor(item.content) && <span className="academy-library-author">{getAuthor(item.content)}</span>}
              {!isBook && <p className="academy-library-excerpt">{stripMarkdown(item.summary || item.content).slice(0, 80)}…</p>}
              <span className="academy-library-cta">查看详情 →</span>
            </div>
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <nav className="academy-library-pagination" aria-label="文档分页">
          <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={safePage === 1}>
            上一页
          </button>
          <span>{safePage} / {totalPages}</span>
          <button type="button" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={safePage === totalPages}>
            下一页
          </button>
        </nav>
      )}

      {!items.length && (
        <p className="academy-library-empty">没有匹配的文档，换个关键词或分类试试。</p>
      )}
    </main>
  );
}
